<template>
  <div class="loyalty-points-messaging">
    <div class="loyalty-points-messaging__message-container">
      <p
        class="loyalty-points-messaging__message"
        v-html="loyaltyPointsMessage"
      />
    </div>

    <div
      v-if="loyaltyCopyInfo"
      class="loyalty-points-messaging__tooltip-container"
    >
      <button
        :aria-controls="ariaControls"
        class="button button--text loyalty-points-messaging__button"
        type="button"
        :aria-expanded="showInfo"
        @click="toggleInfo"
      >
        <span
          class="visually-hidden"
          v-text="$string('product.payment_options.loyalty_points_learn_more')"
        />

        <icon-info />
      </button>

      <p
        v-show="!isMobile && showInfo"
        :id="`loyalty-points-messaging-tooltip-desktop-${namespace}`"
        class="loyalty-points-messaging__tooltip"
        v-text="loyaltyCopyInfo"
      />
    </div>

    <div
      v-show="isMobile && showInfo"
      :id="`loyalty-points-messaging-tooltip-mobile-${namespace}`"
      class="loyalty-points-messaging__tooltip"
    >
      <p
        v-text="loyaltyCopyInfo"
      />

      <button
        :aria-controls="ariaControls"
        class="button button--text loyalty-points-messaging__button"
        type="button"
        :aria-expanded="showInfo"
        @click="toggleInfo"
      >
        <span
          class="visually-hidden"
          v-text="$string('accessibility.close')"
        />

        <icon-close />
      </button>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Loyalty points messaging (loyalty-points-messaging)
 * -----------------------------------------------------------------------------
 * Displays how many APC loyalty points a user would gain based on product
 * price.
 *
 * @param {String} loyaltyCopyInfo - Info for tooltip.
 * @param {String} namespace - Unique namespace.
 * @param {Number} price - Product or variant price.
 *
 */
import { mapState } from 'vuex'

import IconClose from '~icons/directional-navigation/close.svg'
import IconInfo from '~icons/general/info.svg'

export default {
  name: 'LoyaltyPointsMessaging',

  components: {
    IconClose,
    IconInfo,
  },

  props: {
    loyaltyCopyInfo: {
      type: String,
      default: '',
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    price: {
      type: Number,
      default: 0,
      required: true,
    },
  },

  data() {
    return {
      showInfo: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return appropriate aria controls based on screen resolution.
     * @returns {Boolean}
     */
    ariaControls() {
      return this.isMobile
        ? `loyalty-points-messaging-tooltip-mobile-${this.namespace}`
        : `loyalty-points-messaging-tooltip-desktop-${this.namespace}`
    },

    /**
     * Return loyalty points messaging.
     * @returns {String}
     */
    loyaltyPointsMessage() {
      if (!this.price) {
        return this.loyaltyCopyInfo
      }

      const numPoints = this.price / 100

      return this.$string('product.payment_options.loyalty_points_messaging_html')
        .replace('{{ num_points }}', numPoints)
    },
  },

  methods: {

    /**
     * Toggle the state of the info tooltip.
     */
    toggleInfo() {
      this.showInfo = !this.showInfo
    },
  },
}
</script>

<style lang="scss">
@import './loyalty-points-messaging';
</style>
